import { useEffect, useMemo, useState } from 'react';
import { Inbox, Mail, MessageSquare, Send } from 'lucide-react';
import { useStore } from '../hooks/useStore';
import { useActiveRole } from '../state/RoleContext';
import {
  listCommunicationThreads,
  listThreadDeliveries,
  listThreadMessages,
  postThreadReply,
} from '../services/communicationHubService';

interface CommunicationInboxProps {
  mode: 'admin' | 'personal';
  profileId?: string;
}

export function CommunicationInbox({ mode, profileId }: CommunicationInboxProps) {
  const { profile: activeProfile } = useActiveRole();
  const db = useStore((d) => d);
  const [selectedThreadId, setSelectedThreadId] = useState('');
  const [reply, setReply] = useState('');
  const actorProfileId = mode === 'personal' ? profileId : activeProfile.id;

  const threads = useMemo(
    () => listCommunicationThreads(mode === 'personal' && profileId ? { profileId } : {}),
    [db.communicationThreads, db.communicationThreadMessages, mode, profileId],
  );
  const selectedThread = threads.find((thread) => thread.id === selectedThreadId) ?? threads[0];
  const messages = selectedThread ? listThreadMessages(selectedThread.id) : [];
  const deliveries = selectedThread ? listThreadDeliveries(selectedThread.id) : [];

  useEffect(() => {
    if (!selectedThreadId && threads[0]) setSelectedThreadId(threads[0].id);
    if (selectedThreadId && !threads.some((thread) => thread.id === selectedThreadId)) {
      setSelectedThreadId(threads[0]?.id ?? '');
    }
  }, [selectedThreadId, threads]);

  const onReply = () => {
    if (!selectedThread || !actorProfileId || !reply.trim()) return;
    postThreadReply({
      threadId: selectedThread.id,
      authorProfileId: actorProfileId,
      body: reply,
      direction: mode === 'admin' ? 'outbound' : 'inbound',
    });
    setReply('');
  };

  if (mode === 'personal' && !profileId) {
    return (
      <div className="empty">
        <Inbox size={18} />
        <div style={{ fontWeight: 700, color: 'var(--text)', marginTop: 8 }}>No directory profile matched this sign-in.</div>
        <p className="muted" style={{ marginTop: 6 }}>
          Ask your league admin to add your email to the Directory so your Scorekeeper inbox can connect to your messages.
        </p>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="empty">
        <Inbox size={18} />
        <div style={{ fontWeight: 700, color: 'var(--text)', marginTop: 8 }}>No inbox threads yet.</div>
        <p className="muted" style={{ marginTop: 6 }}>
          Sent communications and in-app replies will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid--2">
      <div className="card">
        <div className="card__head">
          <div>
            <h3 className="card__title">
              <Inbox size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
              Inbox
            </h3>
            <p className="card__sub">{threads.length} thread{threads.length === 1 ? '' : 's'}</p>
          </div>
        </div>
        <div className="stack">
          {threads.map((thread) => {
            const tournament = thread.tournamentId ? db.tournaments.find((t) => t.id === thread.tournamentId) : null;
            const count = db.communicationThreadMessages.filter((message) => message.threadId === thread.id).length;
            const scopeLabel = thread.scope === 'league'
              ? 'Full Directory'
              : tournament?.name ?? 'Event audience';
            return (
              <button
                key={thread.id}
                className={`thread-row ${selectedThread?.id === thread.id ? 'is-active' : ''}`}
                onClick={() => setSelectedThreadId(thread.id)}
                type="button"
              >
                <span>
                  <strong>{thread.subject}</strong>
                  <span className="muted">{scopeLabel} - {count} message{count === 1 ? '' : 's'}</span>
                </span>
                <span className="muted">{new Date(thread.lastMessageAt).toLocaleDateString()}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card">
        {selectedThread && (
          <>
            <div className="card__head">
              <div>
                <h3 className="card__title">{selectedThread.subject}</h3>
                <p className="card__sub">
                  {selectedThread.recipientProfileIds.length} recipient{selectedThread.recipientProfileIds.length === 1 ? '' : 's'} - {selectedThread.status}
                </p>
              </div>
            </div>

            <div className="stack" style={{ marginBottom: 14 }}>
              {messages.map((message) => {
                const author = message.authorProfileId
                  ? db.profiles.find((profile) => profile.id === message.authorProfileId)
                  : null;
                return (
                  <div key={message.id} className="message-bubble">
                    <div className="row row--between" style={{ gap: 10 }}>
                      <strong>{author?.displayName ?? message.authorName ?? 'League message'}</strong>
                      <span className="muted" style={{ fontSize: 11 }}>{new Date(message.createdAt).toLocaleString()}</span>
                    </div>
                    {message.subject && <div style={{ fontWeight: 700, marginTop: 6 }}>{message.subject}</div>}
                    <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{message.body}</p>
                    <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {message.channels.includes('email') && <span className="chip"><Mail size={11} /> email</span>}
                      {message.channels.includes('in_app') && <span className="chip"><MessageSquare size={11} /> in-app</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {mode === 'admin' && deliveries.length > 0 && (
              <div className="note" style={{ marginBottom: 12 }}>
                Delivery audit: {deliveries.filter((d) => d.status === 'sent').length} sent,{' '}
                {deliveries.filter((d) => d.status === 'queued').length} queued,{' '}
                {deliveries.filter((d) => d.status === 'skipped').length} skipped,{' '}
                {deliveries.filter((d) => d.status === 'failed').length} failed.
              </div>
            )}

            <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <textarea
                className="textarea"
                rows={3}
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                placeholder="Reply in the app..."
                style={{ flex: 1 }}
              />
              <button className="btn btn--primary" disabled={!reply.trim() || !actorProfileId} onClick={onReply}>
                <Send size={14} /> Reply
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
